// Login page — calls auth.login() and bounces to the saved ?next= or "/".
//
// Visual: tight centred card matching the studio's brand palette. The form is
// the only interaction; OAuth (Google/Microsoft/Facebook/X) is wired through
// the public-auth router and rendered as a list of buttons that full-page
// navigate to /api/v1/auth/oauth/<provider>/start.

import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button, VocalerLogo } from "../../lib/brand";
import { TextField, useToast } from "../../lib/ui";
import { useAuth } from "../../lib/auth/AuthContext";
import { ApiError } from "../../types";
import { env } from "../../config/env";
import { decodeLoginOptions, encodeCredentialForLogin } from "../settings/TwoFactorPanel";

interface OAuthProvider {
  id: string;
  label: string;
}

export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // `null` = still loading; `[]` = backend confirmed none configured (render
  // nothing); `providersError` = the fetch failed after retries — show a
  // recoverable notice instead of silently hiding social sign-in, so a
  // transient API blip (e.g. a deploy window) can't masquerade as "the
  // Google/Microsoft buttons were removed".
  const [providers, setProviders] = useState<OAuthProvider[] | null>(null);
  const [providersError, setProvidersError] = useState(false);
  const [mfa, setMfa] = useState<{ challengeToken: string } | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);

  const next = new URLSearchParams(location.search).get("next") ?? "/";

  useEffect(() => {
    // Already authenticated — go straight home.
    if (auth.status === "authenticated") {
      navigate(next, { replace: true });
    }
  }, [auth.status, navigate, next]);

  // Load the configured social providers, retrying transient failures with
  // backoff. A 2xx with an empty list is a definitive "none configured" (no
  // error); only an exhausted run of network errors / non-2xx flips
  // providersError so the UI can offer a retry rather than hide the buttons.
  const loadProviders = useCallback(async (signal?: { cancelled: boolean }) => {
    const delays = [600, 1500, 3000]; // attempt 1 immediate, then back off
    if (!signal?.cancelled) setProvidersError(false);
    for (let attempt = 0; attempt <= delays.length; attempt += 1) {
      try {
        const res = await fetch(`${env.authBaseUrl}/oauth/providers`, { credentials: "include" });
        if (res.ok) {
          const body = (await res.json()) as { providers?: OAuthProvider[] };
          if (!signal?.cancelled) {
            setProviders(body.providers ?? []);
            setProvidersError(false);
          }
          return;
        }
        // Non-2xx (e.g. a 502 while the API is mid-deploy) — fall through to retry.
      } catch {
        // Network/CORS error — fall through to retry.
      }
      if (signal?.cancelled) return;
      const wait = delays[attempt];
      if (wait === undefined) break; // attempts exhausted
      await new Promise((r) => setTimeout(r, wait));
    }
    if (!signal?.cancelled) {
      setProviders([]);
      setProvidersError(true);
    }
  }, []);

  useEffect(() => {
    const signal = { cancelled: false };
    void loadProviders(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [loadProviders]);

  const retryProviders = useCallback(() => {
    setProviders(null);
    setProvidersError(false);
    void loadProviders();
  }, [loadProviders]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const challenge = await auth.login(email.trim().toLowerCase(), password);
      if (challenge) {
        setMfa(challenge);
        return;
      }
      navigate(next, { replace: true });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Could not sign in";
      toast.notify({ message: msg, state: "error" });
    } finally {
      setSubmitting(false);
    }
  }

  async function verifyMfa() {
    if (!mfa) return;
    setSubmitting(true);
    try {
      const path = useRecovery ? "/auth/2fa/totp/recovery" : "/auth/2fa/totp/verify";
      const body = useRecovery
        ? { challengeToken: mfa.challengeToken, recoveryCode: mfaCode.trim() }
        : { challengeToken: mfa.challengeToken, code: mfaCode.trim() };
      const res = await fetch(`${env.apiBaseUrl.replace(/\/web$/, "")}${path}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        toast.notify({ state: "error", message: body.detail ?? "Code didn't verify." });
        return;
      }
      // The server installed voc_access + voc_refresh cookies via Set-Cookie;
      // we just re-hydrate identity.
      await auth.completeMfa();
      navigate(next, { replace: true });
    } catch (err) {
      toast.notify({ state: "error", message: err instanceof Error ? err.message : "Failed." });
    } finally {
      setSubmitting(false);
    }
  }

  async function loginWithPasskey() {
    if (!window.PublicKeyCredential) {
      toast.notify({ state: "error", message: "This browser doesn't support passkeys." });
      return;
    }
    const targetEmail = email.trim().toLowerCase();
    if (!targetEmail) {
      toast.notify({ state: "error", message: "Enter your email first." });
      return;
    }
    setSubmitting(true);
    try {
      const startRes = await fetch(`${env.apiBaseUrl.replace(/\/web$/, "")}/auth/2fa/passkey/login/start`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: targetEmail }),
      });
      if (!startRes.ok) throw new Error("Couldn't start passkey login.");
      const { options, challengeToken } = await startRes.json();
      const cred = await navigator.credentials.get({ publicKey: decodeLoginOptions(options) });
      if (!cred) throw new Error("No passkey selected.");
      const payload = encodeCredentialForLogin(cred as PublicKeyCredential);
      const completeRes = await fetch(`${env.apiBaseUrl.replace(/\/web$/, "")}/auth/2fa/passkey/login/complete`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ challengeToken, credential: payload }),
      });
      if (!completeRes.ok) {
        const body = await completeRes.json().catch(() => ({}));
        throw new Error(body.detail ?? "Passkey login failed.");
      }
      // Server set the session cookies on its response — re-hydrate identity.
      await auth.completeMfa();
      navigate(next, { replace: true });
    } catch (err) {
      toast.notify({ state: "error", message: err instanceof Error ? err.message : "Failed." });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: "var(--bg)",
        padding: "48px 16px",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 380,
          background: "var(--bg-elev)",
          border: "1px solid var(--border)",
          borderRadius: 8,
          padding: "32px 28px 28px",
        }}
      >
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 28 }}>
          <VocalerLogo />
        </div>
        <h1
          style={{
            fontSize: 22,
            fontWeight: 500,
            letterSpacing: "-0.01em",
            margin: "0 0 4px",
            color: "var(--fg)",
            textAlign: "center",
          }}
        >
          Welcome back.
        </h1>
        <p
          style={{
            fontSize: 13,
            color: "var(--fg-muted)",
            textAlign: "center",
            margin: "0 0 24px",
          }}
        >
          Sign in to keep rendering.
        </p>

        {mfa ? (
          <div style={{ display: "grid", gap: 12 }}>
            <h2 style={{ fontSize: 16, fontWeight: 500, textAlign: "center", margin: "0 0 4px" }}>
              {useRecovery ? "Enter a recovery code" : "Enter your authenticator code"}
            </h2>
            <p style={{ fontSize: 12, color: "var(--fg-muted)", textAlign: "center", margin: "0 0 8px" }}>
              {useRecovery ? "Each recovery code works once." : "6-digit code from 1Password, Authenticator, or your TOTP app."}
            </p>
            <TextField
              label={useRecovery ? "Recovery code" : "6-digit code"}
              value={mfaCode}
              autoComplete="one-time-code"
              inputMode={useRecovery ? "text" : "numeric"}
              onChange={(e) => setMfaCode(useRecovery ? e.currentTarget.value : e.currentTarget.value.replace(/\D/g, "").slice(0, 6))}
              autoFocus
            />
            <Button variant="primary" disabled={submitting || !mfaCode} onClick={verifyMfa}>
              {submitting ? "Verifying…" : "Verify"}
            </Button>
            <button
              onClick={() => { setUseRecovery((v) => !v); setMfaCode(""); }}
              style={{ background: "none", border: "none", color: "var(--fg-muted)", fontSize: 12, cursor: "pointer", marginTop: 4 }}
            >
              {useRecovery ? "← Use authenticator code instead" : "Lost your authenticator? Use a recovery code →"}
            </button>
          </div>
        ) : (<>
        {providers && providers.length > 0 ? (
          <div style={{ display: "grid", gap: 8, marginBottom: 20 }}>
            {providers.map((p) => (
              <a
                key={p.id}
                href={`${env.authBaseUrl}/oauth/${p.id}/start`}
                style={{
                  display: "block",
                  textAlign: "center",
                  textDecoration: "none",
                  padding: "10px 12px",
                  background: "var(--bg)",
                  border: "1px solid var(--border)",
                  borderRadius: 6,
                  color: "var(--fg)",
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                Continue with {p.label}
              </a>
            ))}
            <div
              style={{
                fontSize: 11,
                textAlign: "center",
                color: "var(--fg-subtle)",
                margin: "8px 0 0",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              or with email
            </div>
          </div>
        ) : providersError ? (
          <div
            style={{
              fontSize: 12,
              textAlign: "center",
              color: "var(--fg-muted)",
              background: "var(--bg)",
              border: "1px solid var(--border)",
              borderRadius: 6,
              padding: "8px 12px",
              marginBottom: 20,
            }}
          >
            Social sign-in is temporarily unavailable.{" "}
            <button
              type="button"
              onClick={retryProviders}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                color: "var(--fg)",
                fontSize: 12,
                fontWeight: 500,
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              Retry
            </button>
            <div style={{ marginTop: 2, color: "var(--fg-subtle)" }}>
              You can still sign in with email or a passkey.
            </div>
          </div>
        ) : null}

        <form onSubmit={onSubmit} style={{ display: "grid", gap: 12 }}>
          <TextField
            label="Email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
          />
          <TextField
            label="Password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
          />
          <div style={{ fontSize: 12, textAlign: "right", marginTop: -4 }}>
            <Link to="/forgot" style={{ color: "var(--fg-muted)" }}>Forgot password?</Link>
          </div>
          <Button
            type="submit"
            variant="primary"
            disabled={submitting || !email || !password}
            style={{ marginTop: 4 }}
          >
            {submitting ? "Signing in…" : "Sign in"}
          </Button>
        </form>
        <div style={{ marginTop: 10 }}>
          <Button variant="ghost" disabled={submitting} onClick={loginWithPasskey} style={{ width: "100%" }}>
            Sign in with passkey
          </Button>
        </div>
        <p style={{ fontSize: 13, textAlign: "center", marginTop: 16, color: "var(--fg-muted)" }}>
          New to Vocaler?{" "}
          <Link to={`/signup${location.search}`} style={{ color: "var(--fg)" }}>
            Create an account
          </Link>
        </p>
        </>)}
      </div>
    </div>
  );
}
